import './AddEmployee.scss'
import React, { useEffect, useState } from 'react'
import { browserHistory } from 'react-router'
import Helmet from 'react-helmet'
import { inject } from 'mobx-react'
import { observer } from 'mobx-react-lite'
import { Row, Col, Button, Modal, Alert, message, Spin, Tag } from 'antd-v4'
import Paragraph from 'antd-v4/lib/typography/Paragraph'
import { CloseCircleFilled } from '@ant-design/icons'
import { addEmployee } from '@getgreenline/employees'
import {
  UpdateUser,
  CreateEntityAssignment,
  PermissionNames,
  IUserResponse,
} from '@getgreenline/homi-shared'
import { UserStatus, IGetUserStatusResponseContract } from '@getgreenline/authn'
import { CurrentCompanyStore } from '../../../stores/CurrentCompanyStore'
import { Routes } from '../../../constants/Routes'
import AppHeader from '../../../components/AppView/Header'
import { AppView, AppMainView } from '../../../components/AppView'
import { parseErrorMsg } from '../../../utilities/helpers'
import { SaveButton } from '../../../components/Button/SaveButton'
import { CurrentUserStore } from '../../../stores/CurrentUserStore'
import { NoPermissionsResult } from '../../../components/NoPermissionsResult'
import { SecurityRoleOptions } from './SecurityRoleOptions'
import { parseCongitoError } from '../../../utilities/cognito'

export const ADMINISTRATOR = 'Administrator'

interface Props {
  location: { query: { userId?: string } }
  currentCompanyStore: CurrentCompanyStore
  currentUserStore: CurrentUserStore
}

interface CognitoUser extends UpdateUser {
  cognitoUserId?: string | null
}

export const AddEmployee: React.FC<Props> = inject(
  'currentCompanyStore',
  'currentUserStore',
)(
  observer(({ currentCompanyStore, currentUserStore, location }: Props) => {
    if (!currentCompanyStore.company) return <>{browserHistory.push(Routes.LOGIN)}</>
    const { company } = currentCompanyStore
    const { userId } = location.query
    const [user] = useState<CognitoUser>(new UpdateUser())
    const [isLoadingUser, setIsLoadingUser] = useState(!!userId)
    const [isSaving, setIsSaving] = useState(false)
    const [showModal, setShowModal] = useState(false)
    const [isEditingPassword, setIsEditingPassword] = useState(false)
    const [originalEmail, setOriginalEmail] = useState(user.email)
    const [isForceChangePassword, setIsForceChangePassword] = useState(false)
    const [hasInviteExpired, setHasInviteExpired] = useState(false)
    const setUserAttribute: React.ChangeEventHandler<HTMLInputElement> = ({
      target: { name, value },
    }) => {
      Object.assign(
        user,
        { [name]: value },
        { showPassword: isEditingPassword }, // only updates password if edited
      )
    }

    const hasEmailChanged = originalEmail !== user.email
    const userCanManageEmployees = currentUserStore.hasPermissionAtEntity(
      PermissionNames.CAN_MANAGE_EMPLOYEES,
      company.id,
    )
    const isCognitoUser = user.cognitoUserId !== null
    const isAddingUser = !userId
    const isEditingUser = !isAddingUser
    const isEditingCognitoUser = isEditingUser && isCognitoUser
    const title = isEditingUser ? 'Edit employee' : 'Add employee'

    const checkSecurityRolesErrors = () => {
      if (!user.isValid || !user.entityAssignments.length) return 'Check your location assignments'
      const locationsWithoutSecurityRoles = user.entityAssignments.filter(
        (assignment) => assignment.entity.id === undefined,
      )
      if (locationsWithoutSecurityRoles.length) return 'Assign a security role to your location'

      return false
    }

    const editEmployee = () => currentCompanyStore.editEntityUser(user.id, user.getUpdateObject())

    const createEmployee = async () =>
      addEmployee(company.id, {
        ...user.getCreateObject(),
        entityAssignments: user.entityAssignments.map((ea) => ({
          entity: { id: ea.entity.id as number },
          securityRoleId: ea.securityRoleId,
          userId: user.id,
        })),
      })

    const saveEmployee = async () => {
      try {
        if (!user.name.length) throw new Error('Please enter a name for this employee')
        if (isEditingPassword && !user.password) {
          throw new Error('Please enter a dashboard password or click hide password')
        }
        if ((isAddingUser || isEditingCognitoUser) && user.email?.includes('+')) {
          // Related to Cognito issue https://github.com/aws-amplify/amplify-js/issues/3857
          throw new Error('Email cannot contain "+"')
        }
        const securityRolesError = checkSecurityRolesErrors()
        if (securityRolesError) throw new Error(securityRolesError)

        setIsSaving(true)

        if (isAddingUser) {
          const createdEmployee = await createEmployee()
          const navigateTo = createdEmployee.id
            ? Routes.DASHBOARD_EDIT_EMPLOYEE(company.id, createdEmployee.id)
            : Routes.DASHBOARD_EMPLOYEES(company.id)
          browserHistory.push(navigateTo)
          setIsLoadingUser(true)
        } else {
          await editEmployee()
        }
        if (isEditingCognitoUser && hasEmailChanged && isForceChangePassword) {
          await resendInvite()
        }
        message.success('Saved employee')
      } catch (error) {
        message.error(parseErrorMsg(error) || 'There was an error saving this user')
      } finally {
        setIsSaving(false)
        setOriginalEmail(user.email)
      }
    }

    const getUserCanEditEmployee = () => {
      const updateUserEntityAssignments = user.entityAssignments
      const currentUser = currentUserStore.currentUser
      const companySecurityRoles = currentCompanyStore.securityRoles || []

      if (!currentUser || !companySecurityRoles) return false
      if (currentUser.isAdmin) return true

      // organization users don't use the same security role in each company
      const companyAdminSecurityRole = companySecurityRoles.find(
        (securityRole) => securityRole.name === 'Administrator',
      )

      if (!companyAdminSecurityRole) return false

      const updateUserIsAdmin = updateUserEntityAssignments.some(
        (userEntityAssignment) =>
          userEntityAssignment.securityRoleId === companyAdminSecurityRole.id,
      )
      /**
       * If user is an organization user, they should be classed as Admin until we solve
       * security groups across organizations and companies
       */
      const currentUserIsAdmin = currentUser.entityAssignments.some(
        (userEntityAssignment) =>
          userEntityAssignment.securityRoleId === companyAdminSecurityRole.id ||
          userEntityAssignment.entity.entityType === 'organization',
      )
      if (updateUserIsAdmin && !currentUserIsAdmin) return false
      return true
    }

    const userCanEditEmployee = getUserCanEditEmployee()

    const resetPassword = async () => {
      try {
        if (!user.email) throw new Error('Please enter an email address')
        if (user.email.includes('+'))
          throw new Error(
            'Please change your email to a valid email address first (email cannot contain "+")',
          )
        const lowerCaseEmail = user.email.toLowerCase()
        await CurrentUserStore.forgotPasswordV2(lowerCaseEmail)
        message.success('Password reset email sent')
        setShowModal(false)
      } catch (error) {
        message.error(parseCongitoError(error))
      }
    }

    const resendInvite = async () => {
      try {
        if (!user.id) throw new Error('Something went wrong, please refresh and try again')
        const { User } = await CurrentUserStore.resendInvite(company.id, user.id)
        const email = User?.Attributes?.find(
          ({ Name }: { Name: string }) => Name === 'email',
        )?.Value
        if (!email) {
          throw new Error('There may have been an issue, please check the email address is correct')
        }
        message.success(`Confirmation code sent to ${email}`)
        setShowModal(false)
        setHasInviteExpired(false)
      } catch (error) {
        message.error(`There was an error sending the confirmation code: ${parseErrorMsg(error)}`)
      }
    }

    const assignEntitiesToUser = (entityUser: IUserResponse) => {
      setOriginalEmail(entityUser.email)
      Object.assign(user, {
        ...entityUser,
        entityAssignments: entityUser.entityAssignments.map(
          (ea) =>
            new CreateEntityAssignment({
              ...ea,
              // IUserEntityAssignment.securityRoleId is currently not compatible with CreateEntityAssignment.securityRoleId
              securityRoleId: ea.securityRoleId || undefined,
            }),
        ),
      })
    }

    const evaluateUserStatus = ({ status, updateDate }: IGetUserStatusResponseContract) => {
      if (status === UserStatus.FORCE_CHANGE_PASSWORD) {
        setIsForceChangePassword(true)
      }
      if (!updateDate) return
      const today = new Date()
      const msSinceInviteSent = today.getTime() - new Date(updateDate).getTime()
      const daysSinceInviteSent = Math.floor(msSinceInviteSent / (1000 * 60 * 60 * 24))
      if (status === UserStatus.FORCE_CHANGE_PASSWORD && daysSinceInviteSent >= 1) {
        setHasInviteExpired(true)
      }
    }

    const getEmployeeInfo = async (userId: string) => {
      try {
        const entityUser = await currentCompanyStore.getEntityUserByUserId(userId)
        assignEntitiesToUser(entityUser)
        const userStatus = await CurrentUserStore.getStatus(company.id, userId)
        evaluateUserStatus(userStatus)
      } catch (error) {
        console.error(error)
        if (parseErrorMsg(error) === 'User does not exist.') return // Employee has not logged in since Cognito was added
        message.error(`There was an error getting employee information: ${parseErrorMsg(error)}`)
      } finally {
        setIsLoadingUser(false)
      }
    }

    const HidePassword = () => (
      <a
        onClick={() => setIsEditingPassword(false)}
        className='primary-font-size font-weight-normal'
      >
        <span className='fa fa-minus mr-2' />
        Hide password
      </a>
    )

    const ShowPassword = () => (
      <a
        onClick={() => setIsEditingPassword(true)}
        className='primary-font-size font-weight-normal'
      >
        <span className='fa fa-plus mr-2' />
        Edit password
      </a>
    )

    const EditPassword = () => (
      <>
        <input
          className='form-control mb-2 input-width'
          id='password'
          type='password'
          name='password'
          defaultValue={user.password || ''}
          onBlur={setUserAttribute}
          disabled={!isEditingPassword}
        />
        {isEditingPassword ? <HidePassword /> : <ShowPassword />}
      </>
    )

    const SendNewPassword = () => (
      <Paragraph>An email will be sent to the address above once the employee is saved</Paragraph>
    )

    const ResendInvite = () => (
      <>
        <Button onClick={() => setShowModal(true)} disabled={hasEmailChanged}>
          Resend invite
        </Button>
        {hasInviteExpired && (
          <Tag icon={<CloseCircleFilled />} color='red' className='mt-3 p-1'>
            {' '}
            Signup email has expired, please resend invite to this user.
          </Tag>
        )}
        {hasEmailChanged && (
          <Paragraph className='text-muted mt-3'>Save updates before resending invite</Paragraph>
        )}
      </>
    )

    const ResetPassword = () => (
      <>
        <Button onClick={() => setShowModal(true)} disabled={hasEmailChanged}>
          Reset password
        </Button>
        {hasEmailChanged && (
          <Paragraph className='text-muted mt-3'>Save updates before resetting password</Paragraph>
        )}
      </>
    )

    const PasswordField = () => {
      if (isAddingUser) return <SendNewPassword />
      if (isEditingCognitoUser && isForceChangePassword) return <ResendInvite />
      if (isEditingCognitoUser) return <ResetPassword />
      return <EditPassword />
    }

    useEffect(() => {
      currentCompanyStore.getLocations()
      currentCompanyStore.getSecurityRoles()
      currentUserStore.getCurrentHumanUser()
      if (!userId) return
      getEmployeeInfo(userId)
    }, [userId])

    return !userCanManageEmployees ? (
      <NoPermissionsResult permissionName={PermissionNames.CAN_MANAGE_EMPLOYEES} />
    ) : (
      <AppView column>
        <Helmet>
          <title>Employees | Employee List | Edit</title>
          <meta name='page-loaded' content='true' />
        </Helmet>
        <AppHeader
          backVisible
          onBackClick={() => browserHistory.push(Routes.DASHBOARD_EMPLOYEES_EMPLOYEES(company.id))}
          title={title}
        >
          <SaveButton
            isSaving={isSaving}
            title='Save employee'
            onClick={saveEmployee}
            style={{ minWidth: '200px' }}
            disabled={!userCanEditEmployee}
          />
        </AppHeader>

        <AppMainView>
          {!userCanEditEmployee && (
            <Alert
              showIcon
              type='warning'
              className='mb-3'
              message='Warning'
              description='You must be an administrator to make any changes to this user'
            />
          )}
          <Row className='ml-5 mb-4'>
            <Col xs={12} lg={5} className='mt-5'>
              <label className='font-weight-normal primary-font-black' htmlFor='name'>
                Name <span className='text-danger'>* required</span>
              </label>
            </Col>
            <Col xs={12} lg={6} className='ml-4 mt-5'>
              <input
                className='form-control input-width'
                id='name'
                name='name'
                type='text'
                defaultValue={user.name}
                onBlur={setUserAttribute}
              />
            </Col>
          </Row>
          <Row className='ml-5 mb-4'>
            <Col xs={12} lg={5}>
              <label className='font-weight-normal primary-font-black' htmlFor='registerPasscode'>
                POS passcode
              </label>
              <Paragraph className='text-muted mb-2 primary-font-size'>
                Required to log into the POS
              </Paragraph>
            </Col>
            <Col xs={12} lg={6} className='ml-4'>
              <input
                className='form-control input-width'
                id='registerPasscode'
                name='registerPasscode'
                defaultValue={user.registerPasscode || ''}
                onBlur={setUserAttribute}
              />
            </Col>
          </Row>
          <Row className='ml-5 mb-4'>
            <Col xs={12} lg={5}>
              <label className='font-weight-normal primary-font-black' htmlFor='email'>
                Dashboard email
              </label>
              <Paragraph className='text-muted mb-2 primary-font-size'>
                Required to log into the dashboard
              </Paragraph>
            </Col>
            <Col xs={12} lg={6} className='ml-4'>
              <input
                className='form-control input-width'
                id='email'
                name='email'
                type='email'
                defaultValue={user.email || ''}
                onBlur={setUserAttribute}
              />
            </Col>
          </Row>
          <Row className='ml-5 mb-4'>
            <Col xs={12} lg={5}>
              <label className='font-weight-normal primary-font-black' htmlFor='password'>
                Dashboard password
              </label>
              <Paragraph className='text-muted mb-2 primary-font-size'>
                Required to log into the dashboard
              </Paragraph>
            </Col>
            <Col xs={12} lg={6} className='ml-4'>
              {isLoadingUser ? <Spin className='flex-center' /> : <PasswordField />}
            </Col>
          </Row>
          <Row className='ml-5 mb-4'>
            <SecurityRoleOptions user={user} />
          </Row>

          <Modal
            visible={showModal}
            onOk={() => setShowModal(false)}
            onCancel={() => setShowModal(false)}
            footer={[
              <Button key='no' onClick={() => setShowModal(false)}>
                No
              </Button>,
              <Button
                key='submit'
                type='primary'
                onClick={isForceChangePassword ? resendInvite : resetPassword}
              >
                Yes
              </Button>,
            ]}
          >
            <Alert
              className='mt-3'
              type='warning'
              description={`An email will be sent to ${user.email}`}
              message={`Are you sure you want to ${
                isForceChangePassword
                  ? 'resend the confirmation code to this user?'
                  : 'reset this users password?'
              }`}
              showIcon
            />
          </Modal>
        </AppMainView>
      </AppView>
    )
  }),
)
